<template>
    <div class="dagsoversikt-lista">
        <!-- Add 'rest-header' class if not the first group, otherwise just use the default 'group-header' class -->
        <div :class="['group-header', groupIndex !== 0 ? 'rest-header' : '']">
            <h4>
                Datum: <strong>{{ datum }}</strong>
            </h4>
            <h4>
                Operationssal: <strong>{{ group.sal }}</strong>
            </h4>
        </div>

        <div :key="index" v-for="(anmalan, index) in group.anmalningar" class="anmalan print-no-break">
            <div v-if="anmalan.typoperation" class="anmalan-header">
                <span
                    >Typoperation: <strong>{{ anmalan.current_typoperation_display?.namn }}</strong></span
                >
                <Status
                    v-if="anmalan.preliminarbokning"
                    variant="small"
                    text="Preliminärbokad"
                    :status="true"
                    :icons="{ true: PreliminarbokningIcon }"
                />
                <div class="menu">
                    <KlinikenDropdownMenu
                        :dropdownActions="operationInfoAction"
                        :item="anmalan"
                        @show="tableClicked(anmalan)"
                        @action="dropdownaction"
                    />
                </div>
            </div>
            <div v-else class="anmalan-header">
                <div class="anmalan-header-koder">
                    <div class="anmalan-header-row">
                        <span>Huvudoperationskod:</span>
                        <strong>
                            {{ anmalan.huvudoperationskod_display?.code }}
                            {{ anmalan.huvudoperationskod_display?.displayName }}
                        </strong>
                    </div>
                    <div v-for="(biop, idx) in anmalan.bioperationskoder_display" :key="idx" class="anmalan-header-row">
                        <span>{{ idx === 0 ? "Bioperationskod:" : "" }}</span>
                        <strong>{{ biop.code }} {{ biop.displayName }}</strong>
                    </div>
                </div>
                <Status
                    v-if="anmalan.preliminarbokning"
                    variant="small"
                    text="Preliminärbokad"
                    :status="true"
                    :icons="{ true: PreliminarbokningIcon }"
                />
                <div class="menu">
                    <KlinikenDropdownMenu
                        :dropdownActions="operationInfoAction"
                        :item="anmalan"
                        @show="tableClicked(anmalan)"
                        @action="dropdownaction"
                    />
                </div>
            </div>

            <div class="anmalan-body">
                <table>
                    <tr>
                        <th>PatientID:</th>
                        <td>{{ anmalan.patient_display.personId_formatted }}</td>
                    </tr>
                    <tr>
                        <th>Namn:</th>
                        <td>{{ anmalan.patient_display.namn_reverse }}</td>
                    </tr>

                    <tr v-if="anmalan.diabetes || anmalan.diabetes === false">
                        <th>Diabetes:</th>
                        <td>{{ diabetesText(anmalan) }}</td>
                    </tr>
                    <tr v-if="anmalan.tolk">
                        <th>Tolk:</th>
                        <td>{{ tolkText(anmalan) }}</td>
                    </tr>
                    <tr v-if="anmalan.aktuell_preanestesibedomning?.asa || anmalan.funktionsbedomning">
                        <th>ASA:</th>
                        <td>{{ anmalan.aktuell_preanestesibedomning?.asa ?? anmalan.funktionsbedomning }}</td>
                    </tr>
                    <tr v-if="anmalan.digitum_display?.length">
                        <th>Digitum:</th>
                        <td>
                            <span>{{ anmalan.digitum_display.join(", ") }}</span>
                        </td>
                    </tr>
                </table>

                <table>
                    <tr>
                        <th>Optid start:</th>
                        <td>{{ anmalan.current_operation_tid?.substring(0, 5) }}</td>
                    </tr>
                    <tr>
                        <th>Optid antal min:</th>
                        <td>{{ anmalan.optid }}</td>
                    </tr>
                    <tr>
                        <th>Enhet:</th>
                        <td>
                            {{
                                anmalan.operationsenhet_display?.alias?.length
                                    ? anmalan.operationsenhet_display.alias
                                    : anmalan.operationsenhet_display?.name
                            }}
                        </td>
                    </tr>
                    <tr>
                        <th>Vårdform:</th>
                        <td>{{ anmalan.planerad_vardform_display?.namn }}</td>
                    </tr>
                    <tr v-if="anmalan.avtal_display?.namn">
                        <th>Avtal:</th>
                        <td>{{ anmalan.avtal_display?.namn }}</td>
                    </tr>
                    <tr v-if="anmalan.installelsetid">
                        <th>Inställelsetid:</th>
                        <td>{{ anmalan.installelsetid.substring(0, 5) }}</td>
                    </tr>
                </table>

                <table>
                    <tr>
                        <th>Huvudoperatör:</th>
                        <td v-if="!anmalan.current_bokad_huvudoperator_ej_bestamd">
                            {{ anmalan.current_huvudoperator_display?.user_display?.name }}
                        </td>
                        <td v-else>
                            <span>Ej bestämd</span>
                        </td>
                    </tr>
                    <tr v-if="anmalan.assoperator_ej_bestamd || anmalan.current_ass_operatorer_display?.length">
                        <th>Ass. operatör:</th>
                        <td v-if="!anmalan.assoperator_ej_bestamd">
                            <ul :key="index" v-for="(op, index) in anmalan.current_ass_operatorer_display">
                                <li :key="index">{{ op.user_display.name }}</li>
                            </ul>
                        </td>
                        <td v-else>
                            <span>Ej bestämd</span>
                        </td>
                    </tr>

                    <tr v-if="anmalan.blodsmitta">
                        <th>Blodsmitta:</th>
                        <td>{{ anmalan.blodsmitta === "JA" ? "Ja" : "Nej" }}</td>
                    </tr>
                    <tr v-if="anmalan.multiresistenta_bakterier">
                        <th>Multires. bakt.:</th>
                        <td>{{ anmalan.multiresistenta_bakterier === "JA" ? "Ja" : "Nej" }}</td>
                    </tr>
                    <tr v-if="anmalan.studie_kvalitetsregister_display?.length">
                        <th>Studie/Kvalitetsreg.:</th>
                        <td>
                            <ul>
                                <li v-for="(register, index) in anmalan.studie_kvalitetsregister_display" :key="index">
                                    {{ register.namn }}
                                </li>
                            </ul>
                        </td>
                    </tr>
                </table>

                <table>
                    <tr>
                        <th>Huvuddiagnos:</th>
                        <td>
                            <span
                                v-tooltip="{
                                    content: anmalan.current_huvuddiagnos_display
                                        ? `${anmalan.current_huvuddiagnos_display.code} ${anmalan.current_huvuddiagnos_display.displayName}`
                                        : '',
                                    theme: 'kliniken-tooltip',
                                }"
                            >
                                {{ anmalan.current_huvuddiagnos_display?.code }}
                            </span>
                        </td>
                    </tr>
                    <tr>
                        <th>Huvudoperationskod:</th>
                        <td>
                            <span
                                v-tooltip="{
                                    content: anmalan.huvudoperationskod_display
                                        ? `${anmalan.huvudoperationskod_display.code} ${anmalan.huvudoperationskod_display.displayName}`
                                        : '',
                                    theme: 'kliniken-tooltip',
                                }"
                            >
                                {{ anmalan.huvudoperationskod_display?.code }}
                            </span>
                        </td>
                    </tr>
                    <tr v-if="anmalan.bioperationskoder_display?.length">
                        <th>Bioperationskod:</th>
                        <td>
                            <ul :key="index" v-for="(bioperationskod, index) in anmalan.bioperationskoder_display">
                                <li
                                    v-tooltip="{
                                        content: bioperationskod?.code + ' ' + bioperationskod?.displayName ?? '',
                                        theme: 'kliniken-tooltip',
                                    }"
                                >
                                    {{ bioperationskod.code }}
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="anmalan.reop">
                        <th>Reoperation:</th>
                        <td>{{ anmalan.reop ? "Ja" : "Nej" }}</td>
                    </tr>
                    <tr>
                        <th>Anestesi:</th>
                        <td>{{ getAnestesi(anmalan) }}</td>
                    </tr>
                    <tr v-if="anmalan?.blockad_display?.displayName">
                        <th>Blockad:</th>
                        <td>{{ anmalan.blockad_display.displayName }}</td>
                    </tr>
                    <tr v-if="anmalan?.current_langd && anmalan?.current_vikt">
                        <th>Längd/Vikt (BMI):</th>
                        <td>
                            {{ getNumberAsString(anmalan.current_langd) }} cm,
                            {{ getNumberAsString(anmalan.current_vikt) }} kg ({{
                                calculateBMI(anmalan.current_langd, anmalan.current_vikt)
                            }})
                        </td>
                    </tr>
                </table>

                <table>
                    <tr>
                        <th>Lateralitet:</th>
                        <td>{{ anmalan.lateralitet_display }}</td>
                    </tr>
                    <tr>
                        <th>Patientläge:</th>
                        <td>{{ anmalan.oplage_display?.displayName ?? "-" }}</td>
                    </tr>
                    <tr v-if="anmalan.resurser_display?.length">
                        <th>Resurser:</th>
                        <td>
                            <ul>
                                <li v-for="(resurs, index) in anmalan.resurser_display" :key="index">
                                    {{ getResursWithStatus(resurs) }}
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="anmalan.implantat_display?.length">
                        <th>Implantat:</th>
                        <td>
                            <ul>
                                <li v-for="(implantat, index) in anmalan.implantat_display" :key="index">
                                    {{ implantat.namn }}
                                </li>
                            </ul>
                        </td>
                    </tr>
                    <tr v-if="anmalan.niva_fran_till_display?.length">
                        <th>Nivåer:</th>
                        <td>
                            <NivaFranTill :data="anmalan.niva_fran_till_display" />
                        </td>
                    </tr>
                    <tr v-if="anmalan.hjalpmedel_display?.length">
                        <th>Postop hjälpmedel:</th>
                        <td>
                            <ul>
                                <li v-for="(hjalpmedel, index) in anmalan.hjalpmedel_display" :key="index">
                                    {{ hjalpmedel.displayName }}
                                </li>
                            </ul>
                        </td>
                    </tr>
                </table>
            </div>

            <div v-if="hasFooterContent(anmalan)" class="anmalan-footer">
                <div v-if="anmalan.operationsinformation || anmalan.info_till_koordinering_och_operation">
                    <strong>Info till operation:</strong>
                    {{ anmalan.operationsinformation }}
                    {{ anmalan.info_till_koordinering_och_operation }}
                </div>
                <div v-if="anmalan.aktuell_preanestesibedomning?.lakemedelsallergi">
                    <strong>Läkemedelsallergi:</strong>
                    {{ anmalan.aktuell_preanestesibedomning?.lakemedelsallergi }}
                </div>
                <div v-if="anmalan.aktuell_preanestesibedomning?.ordinationer_operation">
                    <strong>Info till operation från anestesibedömning:</strong>
                    {{ anmalan.aktuell_preanestesibedomning.ordinationer_operation }}
                </div>
                <div v-if="anmalan.aktuell_preanestesibedomning?.ordinationer_avdelning">
                    <strong>Info till avdelning:</strong>
                    {{ anmalan.aktuell_preanestesibedomning.ordinationer_avdelning }}
                </div>
            </div>
        </div>

        <div class="group-footer">
            <div>Totalsumma optid {{ datum }}, {{ sal }}</div>
            <div class="sum-optid">{{ sumOptid }} min</div>
        </div>
    </div>
</template>

<script setup>
    import ActionIconOpanmalan from "@/assets/actionIcons/opanmalan.svg";
    import ActionIconPagaende from "@/assets/actionIcons/pagaendeop.svg";
    import ActionIconShow from "@/assets/actionIcons/show.svg";
    import PreliminarbokningIcon from "@/assets/preliminarbokning.svg";
    import KlinikenDropdownMenu from "@/components/Table/KlinikenDropdownMenu.vue";
    import store from "@/store";
    import Operationsinformation from "@/tabs/Operation/Operationsinformation.vue";
    import Operationsjournal from "@/tabs/Operation/Operationsjournal.vue";
    import PreanestesibedomningVisa from "@/tabs/Operation/PreanestesibedomningVisa.vue";
    import NivaFranTill from "@/tabs/Operation/NivaFranTill.vue";
    import Status from "@/tabs/Operation/Status.vue";
    import { calculateBMI } from "@/components/forms/reusables/BMIHelper";
    import { getResursWithStatus } from "@/components/forms/reusables/resurserHelper";
    import { sumBy } from "lodash";
    import moment from "moment";
    import { computed } from "vue";

    const props = defineProps({
        group: { type: Object, default: () => [] },
        groupIndex: { type: Number, default: 0 },
    });

    const sal = computed(() => {
        return props.group.sal;
    });

    const operationInfoAction = computed(() => {
        return [
            {
                title: "Visa operationsinformation",
                name: "visaOperationsinformation",
                icon: ActionIconOpanmalan,
                show: () => true,
            },
            {
                title: "Starta operation i opjournal",
                name: "visaOperationsjournal",
                icon: ActionIconPagaende,
                show: ({ status, bokad_operation_datum }) => {
                    const today = moment().format("YYYY-MM-DD");

                    return status === "Bokad" && bokad_operation_datum === today;
                },
            },
            {
                title: "Redigera operationsjournal",
                name: "visaOperationsjournal",
                icon: ActionIconPagaende,
                show: ({ status }) => {
                    return ["Pågående", "Genomförd"].includes(status);
                },
            },
            {
                title: "Visa preanestesibedömning",
                name: "visaPreanestesibedömning",
                icon: ActionIconShow,
                show: (item) => {
                    return item.aktuell_preanestesibedomning && item.aktuell_preanestesibedomning;
                },
            },
        ];
    });

    const datum = computed(() => {
        return props.group.anmalningar[0].bokad_operation_datum;
    });

    const getNumberAsString = (number) => {
        return Number(number).toLocaleString("sv-SE");
    };

    function diabetesText(anmalan) {
        if (anmalan.diabetes === null || anmalan.diabetes === undefined) {
            return "-";
        }

        if (anmalan.diabetes) {
            return "Ja";
        } else {
            return "Nej";
        }
    }

    function tolkText(anmalan) {
        if (anmalan.tolk === null || anmalan.tolk === undefined) {
            return "-";
        }

        if (anmalan.tolk === "JA") {
            return "Ja";
        } else {
            return "Nej";
        }
    }

    // TODO: Det här skulle teoretiskt kunna göras i backend (t.ex. som en property current_anestesi) för återanvändbarhet,
    // men har hittills inte fått det att funka.
    function getAnestesi(anmalan) {
        if (anmalan.aktuell_preanestesibedomning?.anestesi__displayName) {
            return anmalan.aktuell_preanestesibedomning.anestesi__displayName;
        } else {
            return anmalan.anestesi_display?.displayName ?? "-";
        }
    }

    function hasFooterContent(anmalan) {
        return Boolean(
            anmalan.operationsinformation ||
            anmalan.info_till_koordinering_och_operation ||
            anmalan.aktuell_preanestesibedomning?.lakemedelsallergi ||
            anmalan.aktuell_preanestesibedomning?.ordinationer_operation ||
            anmalan.aktuell_preanestesibedomning?.ordinationer_avdelning
        );
    }

    function tableClicked(operationsanmalan) {
        if (operationsanmalan.patient) {
            store.dispatch("patientData/loadFromBackend", {
                id: operationsanmalan.patient,
                failSilently: false,
            });
        }
    }

    function openOperationsinformation(operationsanmalan) {
        store.dispatch("tabs/openTab", {
            component: Operationsinformation,
            parameters: { operationsanmalan_id: operationsanmalan.id },
        });
    }

    function openPreanestesibedomning(operationsanmalan) {
        store.dispatch("tabs/openTab", {
            component: PreanestesibedomningVisa,
            parameters: {
                operationsanmalanId: operationsanmalan.id,
                preanestesibedomningId: operationsanmalan.aktuell_preanestesibedomning.id,
            },
        });
    }

    function openOperationsjournal(operationsanmalan) {
        store.dispatch("tabs/openTab", {
            component: Operationsjournal,
            parameters: { operationsanmalan_id: operationsanmalan.id },
        });
    }

    function dropdownaction({ _, item: operationsanmalan, action }) {
        switch (action) {
            case "visaOperationsinformation":
                openOperationsinformation(operationsanmalan);
                break;
            case "visaPreanestesibedömning":
                openPreanestesibedomning(operationsanmalan);
                break;
            case "visaOperationsjournal":
                openOperationsjournal(operationsanmalan);
                break;
        }
    }

    const sumOptid = computed(() => {
        return sumBy(props.group.anmalningar, (anmalan) => anmalan.optid);
    });
</script>

<style lang="scss" scoped>
    @import "@/style/variables";

    .dagsoversikt-lista {
        .menu {
            flex-shrink: 0;
        }

        .group-header {
            display: flex;
            align-items: flex-end;
            gap: 25px;

            h4 {
                /* Reset margin and padding to eliminate any default spacing */
                margin: 0px;
                padding: 0px;

                font-size: $font-size-small;
            }

            strong {
                /* Reset margin and padding to eliminate any default spacing */
                margin: 0px;
                padding: 0px;

                color: $color-active-dark;
                font-size: $font-size-small;
            }
        }

        .rest-header {
            margin: 5px 0px;
        }

        .group-footer {
            display: flex;
            flex-direction: row;
            justify-content: flex-end;
            column-gap: 24px;

            padding: 10px 20px;
            border-top: 2px solid $color-table-header-line;
            border-bottom: 2px solid $color-table-header-line;
            background: linear-gradient(270deg, #f1f4f8 0%, rgba(241, 244, 248, 0) 52.01%);

            font-size: $font-size-small;
            color: $color-active-dark;

            .sum-optid {
                color: black;
                font-family:
                    Roboto Medium,
                    sans-serif;
            }
        }

        .anmalan-header {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            background: $color-bg-table-header;
            border-top: 2px solid $color-table-header-line;
            border-bottom: 2px solid $divider-color;
            padding: 4px;
            font-size: $font-size-small;
            color: $color-active-dark;

            // 2:a barnet är Status om den renderas (v-if), annars menyn — så detta pushar alltid rätt grupp åt höger
            > :nth-child(2) {
                margin-left: auto;
            }

            strong {
                font-size: $font-size-small;
                color: $color-active-dark;
            }

            .anmalan-header-koder {
                display: table;

                .anmalan-header-row {
                    display: table-row;

                    span {
                        display: table-cell;
                        white-space: nowrap;
                        padding-right: 4px;
                        vertical-align: top;
                    }

                    strong {
                        display: table-cell;
                        vertical-align: top;
                    }
                }
            }

            .status,
            .menu {
                flex-shrink: 0;
            }
        }

        .anmalan-body {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 4px;

            column-gap: 24px;

            font-size: $font-size-small;
            line-height: 18px;

            table {
                th {
                    white-space: nowrap;
                    width: 10%;
                    vertical-align: top;
                    font-family:
                        Roboto Medium,
                        sans-serif;
                }

                td {
                    width: 90%;
                    vertical-align: top;
                }
            }
        }

        .anmalan-footer {
            display: flex;
            flex-direction: column;
            gap: 6px;
            border-top: 1px solid $list-border-color;
            padding: 6px;
            font-size: $font-size-small;
            line-height: 16px;

            strong {
                font-size: $font-size-small;
                line-height: 16px;
                font-weight: 700;
            }
        }

        ul {
            list-style-type: none;
            padding: 0;
            margin: 0;
        }

        ::v-deep(.menu) {
            button {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 24px;
                width: 30px;
                border: 1px solid #728296;
                border-radius: 4px;
                background-color: white;
                vertical-align: -webkit-baseline-middle;
                margin-right: 10px;

                img {
                    width: 100%;
                    height: 100%;
                }
            }
        }
    }

    @media print {
        .dagsoversikt-lista + .dagsoversikt-lista {
            break-before: page !important;
            page-break-before: always !important;
        }

        .group-footer {
            padding-top: 4px;
            padding-bottom: 4px;
        }

        .anmalan-body {
            padding-bottom: 2px;
        }
    }
</style>
